<script setup lang="ts">
import { onMounted, ref } from 'vue'
import {
  ApiError,
  bootstrap,
  login,
  registerAccount,
  registrationRequirements,
  request,
  verifyEmail,
} from '../core/api'
import { t } from '../core/i18n'
import { navigate, router } from '../core/router'
import { session, setBootstrap, setSession } from '../core/session'
import type { LoginResponse, RegistrationRequirements } from '../core/types'

type AuthMode = 'login' | 'register' | 'reset-request' | 'reset' | 'recovery'

const REMEMBERED_LOGIN_KEY = 'ci.remembered-login'
const REMEMBER_SESSION_PREFERENCE_KEY = 'ci.remember-session-preference'
const rememberedLogin = localStorage.getItem(REMEMBERED_LOGIN_KEY)

const mode = ref<AuthMode>('login')
const loginName = ref(rememberedLogin ?? '')
const password = ref('')
const rememberMe = ref(
  localStorage.getItem(REMEMBER_SESSION_PREFERENCE_KEY) === '1',
)
const rememberLogin = ref(rememberedLogin !== null)
const totp = ref('')
const resetEmail = ref('')
const resetToken = ref('')
const resetPassword = ref('')
const resetConfirmation = ref('')
const recoveryLogin = ref('')
const recoveryPassword = ref('')
const recoveryCode = ref('')
const registerUsername = ref('')
const registerEmail = ref('')
const registerPassword = ref('')
const registerPasswordConfirmation = ref('')
const minimumAgeConfirmed = ref(false)
const termsAccepted = ref(false)
const privacyAccepted = ref(false)
const requirements = ref<RegistrationRequirements | null>(null)
const busy = ref(false)
const error = ref('')
const success = ref('')

function clearMessages(): void {
  error.value = ''
  success.value = ''
}

function persistLoginPreferences(loginValue = loginName.value): void {
  localStorage.setItem(
    REMEMBER_SESSION_PREFERENCE_KEY,
    rememberMe.value ? '1' : '0',
  )
  if (rememberLogin.value)
    localStorage.setItem(REMEMBERED_LOGIN_KEY, loginValue.trim())
  else localStorage.removeItem(REMEMBERED_LOGIN_KEY)
}

function problemText(caught: unknown): string {
  if (!(caught instanceof ApiError)) return t('errors.load')
  const known: Record<string, string> = {
    ACCOUNT_IDENTIFIER_CONFLICT: t('auth.registerConflict'),
    PASSWORD_INVALID: t('auth.passwordRequirements'),
    PASSWORD_COMPROMISED: t('auth.passwordRequirements'),
    REGISTRATION_LEGAL_ACCEPTANCE_OUTDATED: t('auth.legalOutdated'),
    AUTH_TOKEN_INVALID_OR_EXPIRED: t('auth.tokenInvalid'),
    VALIDATION_FAILED: t('auth.validationFailed'),
    RATE_LIMITED: t('auth.rateLimited'),
  }
  return known[caught.problem.code] ?? caught.message
}

async function loadRequirements(): Promise<boolean> {
  if (requirements.value !== null) return true
  try {
    requirements.value = await registrationRequirements()
    return true
  } catch (caught) {
    error.value = problemText(caught)
    return false
  }
}

async function showRegister(): Promise<void> {
  clearMessages()
  busy.value = true
  try {
    if (await loadRequirements()) mode.value = 'register'
  } finally {
    busy.value = false
  }
}

function showLogin(): void {
  clearMessages()
  mode.value = 'login'
}

function showResetRequest(): void {
  clearMessages()
  mode.value = 'reset-request'
}

function showRecovery(): void {
  clearMessages()
  mode.value = 'recovery'
}

async function submitLogin(): Promise<void> {
  busy.value = true
  clearMessages()
  try {
    const authenticated = await login(
      loginName.value,
      password.value,
      totp.value,
      rememberMe.value,
    )
    persistLoginPreferences(loginName.value)
    setSession(authenticated, rememberMe.value)
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}

async function submitRegistration(): Promise<void> {
  busy.value = true
  clearMessages()
  try {
    if (!(await loadRequirements()) || requirements.value === null) return
    if (
      !termsAccepted.value ||
      !privacyAccepted.value ||
      !minimumAgeConfirmed.value
    ) {
      error.value = t('auth.legalRequired')
      return
    }
    const created = await registerAccount({
      username: registerUsername.value,
      email: registerEmail.value,
      password: registerPassword.value,
      passwordConfirmation: registerPasswordConfirmation.value,
      termsVersion: requirements.value.termsVersion,
      privacyVersion: requirements.value.privacyVersion,
      minimumAgeConfirmed: minimumAgeConfirmed.value,
    })
    loginName.value = created.username
    password.value = ''
    mode.value = 'login'
    success.value = t('auth.registrationCreated')
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}

async function requestReset(): Promise<void> {
  busy.value = true
  clearMessages()
  try {
    await request('/api/v2/password-reset-requests', {
      method: 'POST',
      body: { email: resetEmail.value },
      anonymous: true,
    })
    success.value = t('auth.verificationComplete')
    mode.value = 'reset'
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}
async function submitReset(): Promise<void> {
  busy.value = true
  clearMessages()
  try {
    await request('/api/v2/password-resets', {
      method: 'POST',
      body: {
        token: resetToken.value,
        password: resetPassword.value,
        passwordConfirmation: resetConfirmation.value,
      },
      anonymous: true,
    })
    success.value = t('auth.verificationComplete')
    mode.value = 'login'
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}
async function recoverTwoFactor(): Promise<void> {
  busy.value = true
  clearMessages()
  try {
    const authenticated = await request<LoginResponse>(
      '/api/v2/two-factor-recoveries',
      {
        method: 'POST',
        body: {
          login: recoveryLogin.value,
          password: recoveryPassword.value,
          recoveryCode: recoveryCode.value,
          rememberMe: rememberMe.value,
          deviceType: 'WEB_PWA',
        },
        anonymous: true,
      },
    )
    persistLoginPreferences(recoveryLogin.value)
    setSession(authenticated, rememberMe.value)
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}

async function consumeVerificationToken(token: string): Promise<void> {
  busy.value = true
  clearMessages()
  navigate('/login', true)
  try {
    await verifyEmail(token)
    success.value = t('auth.verificationComplete')
    if (session.token) setBootstrap(await bootstrap())
  } catch (caught) {
    error.value = problemText(caught)
  } finally {
    busy.value = false
  }
}

onMounted(() => {
  const verificationToken = router.current.query.get('verify')
  if (verificationToken) {
    void consumeVerificationToken(verificationToken)
    return
  }
})
</script>

<template>
  <main class="login-layout">
    <section class="login-card panel">
      <div class="login-crest" aria-hidden="true">
        <img src="/assets/craft-imperium/brand/crest.svg" alt="" />
      </div>
      <h1>{{ t('app.title') }}</h1>
      <p class="lead">{{ t('app.tagline') }}</p>
      <p v-if="success" class="success-banner" role="status">{{ success }}</p>
      <p v-if="error" class="error-banner" role="alert">{{ error }}</p>
      <p
        v-else-if="session.endReason === 'expired'"
        class="error-banner"
        role="alert"
      >
        {{ t('auth.sessionExpired') }}
      </p>

      <form v-if="mode === 'login'" @submit.prevent="submitLogin">
        <label>
          <span>{{ t('auth.loginName') }}</span>
          <input
            v-model.trim="loginName"
            name="login"
            autocomplete="username"
            required
            autofocus
          />
        </label>
        <label>
          <span>{{ t('auth.password') }}</span>
          <input
            v-model="password"
            name="password"
            type="password"
            autocomplete="current-password"
            required
          />
        </label>
        <label>
          <span>{{ t('auth.totp') }}</span>
          <input
            v-model.trim="totp"
            name="totp"
            inputmode="numeric"
            autocomplete="one-time-code"
          />
        </label>
        <label class="auth-check">
          <input v-model="rememberMe" type="checkbox" />
          <span>{{ t('auth.rememberMe') }}</span>
        </label>
        <label class="auth-check">
          <input v-model="rememberLogin" type="checkbox" />
          <span>{{ t('auth.rememberLogin') }}</span>
        </label>
        <small class="muted">{{ t('auth.passwordManagerHint') }}</small>
        <button class="primary" type="submit" :disabled="busy">
          {{ busy ? t('common.loading') : t('auth.login') }}
        </button>
        <div class="auth-secondary-actions">
          <button type="button" :disabled="busy" @click="showRegister">
            {{ t('auth.register') }}
          </button>
          <button type="button" :disabled="busy" @click="showResetRequest">
            Passwort vergessen
          </button>
          <button type="button" :disabled="busy" @click="showRecovery">
            2FA Recovery
          </button>
        </div>
      </form>

      <form v-else-if="mode === 'reset-request'" @submit.prevent="requestReset">
        <h2>Passwort zurücksetzen</h2>
        <label
          ><span>E-Mail</span
          ><input
            v-model.trim="resetEmail"
            type="email"
            autocomplete="email"
            required
        /></label>
        <button class="primary" type="submit" :disabled="busy">
          Reset anfordern
        </button>
        <button type="button" @click="showLogin">{{ t('common.back') }}</button>
      </form>
      <form v-else-if="mode === 'reset'" @submit.prevent="submitReset">
        <h2>Neues Passwort</h2>
        <label
          ><span>Reset-Token</span><input v-model.trim="resetToken" required
        /></label>
        <label
          ><span>Neues Passwort</span
          ><input
            v-model="resetPassword"
            type="password"
            autocomplete="new-password"
            required
        /></label>
        <label
          ><span>Bestätigung</span
          ><input
            v-model="resetConfirmation"
            type="password"
            autocomplete="new-password"
            required
        /></label>
        <button class="primary" type="submit" :disabled="busy">
          Passwort setzen
        </button>
        <button type="button" @click="showLogin">{{ t('common.back') }}</button>
      </form>
      <form v-else-if="mode === 'recovery'" @submit.prevent="recoverTwoFactor">
        <h2>2FA Recovery</h2>
        <label
          ><span>Login</span
          ><input v-model.trim="recoveryLogin" autocomplete="username" required
        /></label>
        <label
          ><span>Passwort</span
          ><input
            v-model="recoveryPassword"
            type="password"
            autocomplete="current-password"
            required
        /></label>
        <label
          ><span>Recovery-Code</span
          ><input v-model.trim="recoveryCode" required
        /></label>
        <button class="primary" type="submit" :disabled="busy">
          Recovery starten
        </button>
        <button type="button" @click="showLogin">{{ t('common.back') }}</button>
      </form>

      <form
        v-else-if="mode === 'register'"
        @submit.prevent="submitRegistration"
      >
        <h2>{{ t('auth.registerTitle') }}</h2>
        <label>
          <span>{{ t('auth.username') }}</span>
          <input
            v-model.trim="registerUsername"
            name="username"
            autocomplete="username"
            required
            autofocus
          />
        </label>
        <label>
          <span>{{ t('auth.email') }}</span>
          <input
            v-model.trim="registerEmail"
            name="email"
            type="email"
            autocomplete="email"
            required
          />
        </label>
        <label>
          <span>{{ t('auth.password') }}</span>
          <input
            v-model="registerPassword"
            name="new-password"
            type="password"
            autocomplete="new-password"
            minlength="12"
            required
          />
          <small class="muted">{{ t('auth.passwordRequirements') }}</small>
        </label>
        <label>
          <span>{{ t('auth.passwordConfirmation') }}</span>
          <input
            v-model="registerPasswordConfirmation"
            name="new-password-confirmation"
            type="password"
            autocomplete="new-password"
            minlength="12"
            required
          />
        </label>
        <label class="auth-check"
          ><input
            v-model="minimumAgeConfirmed"
            type="checkbox"
            required
          /><span>{{ t('auth.minimumAge') }}</span></label
        >
        <label class="auth-check"
          ><input v-model="termsAccepted" type="checkbox" required /><span>{{
            t('auth.acceptTerms')
          }}</span></label
        >
        <label class="auth-check"
          ><input v-model="privacyAccepted" type="checkbox" required /><span>{{
            t('auth.acceptPrivacy')
          }}</span></label
        >
        <button class="primary" type="submit" :disabled="busy">
          {{ busy ? t('common.loading') : t('auth.createAccount') }}
        </button>
        <button type="button" :disabled="busy" @click="showLogin">
          {{ t('common.back') }}
        </button>
      </form>
    </section>
  </main>
</template>
